import { useState } from 'react';

interface Diferencial {
  icon: string;
  titulo: string;
  descricao: string;
}

const diferenciais: Diferencial[] = [
  {
    icon: 'ri-award-fill',
    titulo: 'Expertise Setorial',
    descricao:
      'Ampla experiência em assessoria de empresas e pessoas físicas, com conhecimento técnico aprofundado em cada área de atuação.',
  },
  {
    icon: 'ri-shield-flash-line',
    titulo: 'Prevenção de Riscos',
    descricao:
      'Abordagem preventiva e consultiva que antecipa riscos jurídicos antes que se tornem litígios, protegendo patrimônio e reputação.',
  },
  {
    icon: 'ri-user-star-line',
    titulo: 'Atendimento Personalizado',
    descricao:
      'Cada cliente recebe dedicação exclusiva, com estratégias jurídicas construídas de acordo com suas necessidades específicas.',
  },
  {
    icon: 'ri-links-line',
    titulo: 'Atuação Integrada',
    descricao:
      'Visão holística do direito: consultoria preventiva e contenciosa trabalhando juntas para soluções mais sólidas e duradouras.',
  },
  {
    icon: 'ri-trophy-line',
    titulo: 'Foco em Resultados',
    descricao:
      'Comprometimento com o resultado de cada caso, com estratégias claras, prazos cumpridos e comunicação transparente.',
  },
];

const VISIBLE_DESKTOP = 3;

const DiferencialCard = ({ d }: { d: Diferencial }) => (
  <div className="group bg-white border border-[#E5E7EB] px-6 py-5 hover:border-[#C8A96A]/50 transition-all duration-300 cursor-default flex items-start gap-4 h-full">
    <div className="w-9 h-9 flex items-center justify-center bg-[#0F1E2E] flex-shrink-0 group-hover:bg-[#1E3A5F] transition-colors duration-300 mt-0.5">
      <i className={`${d.icon} text-[#C8A96A] text-base`}></i>
    </div>
    <div>
      <h3 className="text-[#0F1E2E] text-sm font-semibold leading-tight mb-1.5">{d.titulo}</h3>
      <p className="text-[#2B2F33]/70 text-xs leading-relaxed">{d.descricao}</p>
    </div>
  </div>
);

const DiferenciaisSection = () => {
  const [current, setCurrent] = useState(0);
  const max = diferenciais.length - VISIBLE_DESKTOP;

  const prev = () => setCurrent((c) => Math.max(c - 1, 0));
  const next = () => setCurrent((c) => Math.min(c + 1, max));

  return (
    <section id="diferenciais" className="py-16 md:py-28 bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#C8A96A]" />
            <span className="text-[#C8A96A] text-xs font-semibold tracking-[0.35em] uppercase">
              Por que nos escolher
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F1E2E] leading-tight">
            Nossos <span className="text-[#C8A96A]">Diferenciais</span>
          </h2>
        </div>

        {/* Mobile: colunas */}
        <div className="flex flex-col gap-4 md:hidden">
          {diferenciais.map((d) => (
            <DiferencialCard key={d.titulo} d={d} />
          ))}
        </div>

        {/* Desktop: carrossel */}
        <div className="hidden md:block">
          <div className="flex items-center gap-4">
            {/* Seta esquerda */}
            <button
              onClick={prev}
              disabled={current === 0}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-[#0F1E2E]/20 text-[#0F1E2E] hover:border-[#C8A96A] hover:text-[#C8A96A] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>

            {/* Cards visíveis */}
            <div className="flex-grow overflow-hidden">
              <div
                className="flex transition-transform duration-400 ease-in-out gap-4"
                style={{ transform: `translateX(calc(-${current} * (100% / ${VISIBLE_DESKTOP} + 16px / ${VISIBLE_DESKTOP})))` }}
              >
                {diferenciais.map((d) => (
                  <div
                    key={d.titulo}
                    className="flex-shrink-0"
                    style={{ width: `calc(100% / ${VISIBLE_DESKTOP} - ${(VISIBLE_DESKTOP - 1) * 16 / VISIBLE_DESKTOP}px)` }}
                  >
                    <DiferencialCard d={d} />
                  </div>
                ))}
              </div>
            </div>

            {/* Seta direita */}
            <button
              onClick={next}
              disabled={current >= max}
              className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-[#0F1E2E]/20 text-[#0F1E2E] hover:border-[#C8A96A] hover:text-[#C8A96A] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
              aria-label="Próximo"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {Array.from({ length: max + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all duration-200 cursor-pointer ${
                  i === current ? 'bg-[#C8A96A] w-4' : 'bg-[#0F1E2E]/20 w-1.5'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiferenciaisSection;
